import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Empty, Modal, Row, Spin, Typography } from 'antd';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { HeadquarterSelector } from './HeadquarterSelector';
import { WorkStationSelector } from './WorkStationSelector';
import { WorkStationCheckinSuccessModal } from './WorkStationCheckinSuccessModal';
import { useWorkStationCheckin } from '../hooks/useWorkStationCheckin';
import { useHeadquarterSelector } from '../hooks/useHeadquarterSelector';
import { PRIMARY_COLOR, TodayLocationLabel, GreetingTitle, QuestionTitle, LastRecordBanner } from '../styles';
import { useDictionaryTranslation } from '../../../hooks/useDictionaryTranslation';

const { Text } = Typography;

const getFormattedDate = (format) => {
  const raw = moment().format(format);
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const WorkStationCheckinModal = ({ show, onSave, onClose }) => {
  const { translate: t } = useDictionaryTranslation();
  const currentUser = useSelector((state) => state.auth.currentUser);
  const { lastRecord, isLoadingLastRecord, isCreating, createRecord } = useWorkStationCheckin();
  const { headquarters, isLoading: isLoadingHeadquarters } = useHeadquarterSelector();

  const [selectedHeadquarter, setSelectedHeadquarter] = useState(null);
  const [selectedWorkStation, setSelectedWorkStation] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const firstName = (currentUser?.profile?.name ?? '').split(' ')[0];
  const canConfirm = selectedHeadquarter != null && selectedWorkStation != null;
  const suggestedIdWorkStation = lastRecord != null ? lastRecord.idWorkStation : null;
  const isLastRecordHeadquarterAvailable =
    lastRecord != null && headquarters.some((headquarter) => headquarter._id === lastRecord.headquarterData?._id);

  const handleSelectHeadquarter = (headquarter) => {
    setSelectedHeadquarter(headquarter);
    setSelectedWorkStation(null);
  };

  const handleUseLast = () => {
    setSelectedHeadquarter(lastRecord.headquarterData);
    setSelectedWorkStation(lastRecord.workStationData);
  };

  const handleConfirm = async () => {
    try {
      await createRecord({
        idWorkStation: selectedWorkStation._id,
        idHeadquarter: selectedHeadquarter._id,
      });
      onSave();
      setIsSuccess(true);
    } catch {
      // error notification is shown by the hook
    }
  };

  const handleReset = () => {
    setSelectedHeadquarter(null);
    setSelectedWorkStation(null);
    setIsSuccess(false);
  };

  return (
    <>
      <Modal
        open={show && !isSuccess}
        closable={false}
        maskClosable={false}
        centered
        width="60%"
        title={<TodayLocationLabel>{t('checkinTodayLocation')}</TodayLocationLabel>}
        footer={
          <Row justify="end" align="middle">
            <Button type="primary" disabled={!canConfirm} loading={isCreating} onClick={handleConfirm}>
              {t('checkinConfirm')}
            </Button>
          </Row>
        }
      >
        <Spin spinning={isLoadingLastRecord}>
          <GreetingTitle level={3}>
            {t('checkinGreeting')}, {firstName}
          </GreetingTitle>
          <Text type="secondary">{getFormattedDate(t('checkinDateFormat'))}</Text>

          <QuestionTitle level={5}>{t('checkinQuestion')}</QuestionTitle>
          <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
            {lastRecord != null ? (
              <Col span={24}>
                <LastRecordBanner>
                  <Text>
                    {t('checkinLastTime')}:{' '}
                    <Text strong style={{ color: PRIMARY_COLOR }}>
                      {lastRecord.headquarterData?.name} · {lastRecord.workStationData?.name}
                    </Text>
                  </Text>
                  {isLastRecordHeadquarterAvailable ? (
                    <Button type="primary" size="small" onClick={handleUseLast}>
                      {t('checkinUseLast')}
                    </Button>
                  ) : null}
                </LastRecordBanner>
              </Col>
            ) : null}

            <Col span={24}>
              <HeadquarterSelector
                headquarters={headquarters}
                isLoading={isLoadingHeadquarters}
                selectedHeadquarter={selectedHeadquarter}
                onSelect={handleSelectHeadquarter}
              />
            </Col>

            <Col span={24}>
              {selectedHeadquarter != null ? (
                <WorkStationSelector
                  idHeadquarter={selectedHeadquarter._id}
                  selectedWorkStation={selectedWorkStation}
                  onSelect={setSelectedWorkStation}
                  suggestedIdWorkStation={suggestedIdWorkStation}
                />
              ) : (
                <Empty description={t('checkinSelectHeadquarterFirst')} />
              )}
            </Col>
          </Row>
        </Spin>
      </Modal>

      <WorkStationCheckinSuccessModal
        show={show && isSuccess}
        firstName={firstName}
        headquarterName={selectedHeadquarter?.name}
        workStationName={selectedWorkStation?.name}
        onClose={onClose}
        onReset={handleReset}
      />
    </>
  );
};

WorkStationCheckinModal.propTypes = {
  show: PropTypes.bool,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
};

export { WorkStationCheckinModal };
